/**
 * Delivery Lead-Time Manager  (V2.0 / 2026-06-16)
 * ------------------------------------------------------------------
 * 한정판매(태그 `leadtime`) 상품의 배송일을 메타오브젝트 `limited_drop`에서 읽어
 * 캘린더를 그 날짜 하나로 제한하고, 판매 마감 시각 이후 구매를 막는다.
 *
 *   - 로컬 손님(LA_OC)  → limited_drop.local_delivery_date 그날만
 *   - 냉동 손님(SOCAL/NATIONWIDE/미등록) → limited_drop.frozen_delivery_date 그날만
 *   - 판매 마감 = 로컬 배송일 오전 5시(LA). 이후 leadtime 상품 '주문 마감' 처리.
 *
 * 날짜는 코드에 없음 — Admin 메타오브젝트에서만 관리(드롭마다 항목 1개).
 * theme.liquid 가 metaobject 를 window.limitedDrop 로 주입한다:
 *   window.limitedDrop = [{ local:"YYYY-MM-DD", frozen:"YYYY-MM-DD" }, ...]
 *
 * 의존: window.ChefBanchan.ZipCodeManager (zone), window.limitedDrop (날짜)
 * 안전: 이 스크립트는 reload/카트변경/재고변경을 하지 않음(캘린더 숨김 + 버튼 비활성만).
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.DeliveryLeadtimeManager) return; // 중복 로드 방지

  // ============================================================
  // ⚙️ 설정
  // ============================================================
  var CONFIG = {
    tag: 'leadtime',          // 한정 상품 태그
    localZone: 'LA_OC',       // 로컬로 취급할 zone
    cutoffHour: 5,            // 로컬 배송일 이 시각(LA, 24h) 이후 판매 마감
    showNotice: true,         // 캘린더 위 안내 문구
    noticeText: 'Limited item in cart — delivers {date} only.', // {date} 치환
    closedText: 'Order closed — this limited item is no longer available.', // 마감 후 버튼/문구
    debug: true               // 콘솔 로그 (테스트 끝나면 false 권장)
  };

  function log() {
    if (CONFIG.debug && window.console) {
      console.log.apply(console, ['🕒 [Leadtime]'].concat([].slice.call(arguments)));
    }
  }

  // ============================================================
  // 시간/날짜 — 가게 기준(LA) 시간 고정
  // ============================================================
  function laNow() {
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  }

  // ============================================================
  // 현재 드롭 — window.limitedDrop 중 로컬 배송일이 가장 늦은(=최신) 항목
  // ============================================================
  function getDrop() {
    var drops = window.limitedDrop;
    if (!drops) return null;
    if (!Array.isArray(drops)) drops = [drops];
    var best = null;
    for (var i = 0; i < drops.length; i++) {
      var d = drops[i];
      if (!d || !d.local) continue;
      if (!best || d.local > best.local) best = d;
    }
    return best;
  }

  // ============================================================
  // zone — localStorage zip → 없으면 장바구니 속성(_order_tags: ZIP_xxxxx)
  // ============================================================
  var cartZip = '';

  function getZone() {
    var zip = '';
    try {
      var zm = window.ChefBanchan && window.ChefBanchan.ZipCodeManager;
      if (zm && typeof zm.getCurrentZipCode === 'function') zip = zm.getCurrentZipCode() || '';
      if (!zip) zip = cartZip;
      if (zip && zm && typeof zm.getDeliveryZone === 'function') return zm.getDeliveryZone(zip);
    } catch (e) { /* noop */ }
    return localStorage.getItem('chefBanchanZone') || 'NATIONWIDE';
  }

  // 이 손님(zone)에게 허용된 목표 배송일 (YYYY-MM-DD). 드롭 없으면 null.
  function getTargetStr() {
    var drop = getDrop();
    if (!drop) return null;
    var t = (getZone() === CONFIG.localZone) ? drop.local : drop.frozen;
    return t || null;
  }

  // 판매 마감 여부 — 로컬 배송일 cutoffHour(05시) 이후면 true
  function isClosed() {
    var drop = getDrop();
    if (!drop || !drop.local) return false;
    var p = drop.local.split('-');
    var cutoff = new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10), CONFIG.cutoffHour, 0, 0);
    return laNow() >= cutoff;
  }

  // ============================================================
  // 상품 태그 확인 (handle별 캐시)
  // ============================================================
  var tagCache = {};

  function productHasTag(handle) {
    if (tagCache.hasOwnProperty(handle)) return Promise.resolve(tagCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var has = false;
        if (p && Array.isArray(p.tags)) {
          var want = CONFIG.tag.toLowerCase();
          has = p.tags.some(function (t) { return String(t).toLowerCase() === want; });
        }
        tagCache[handle] = has;
        return has;
      })
      .catch(function () { tagCache[handle] = false; return false; });
  }

  // ============================================================
  // 장바구니 재평가 → ruleActive (카트에 leadtime 상품 있나)
  // ============================================================
  var ruleActive = false;
  var lastFingerprint = null;

  function refresh() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var attrs = (cart && cart.attributes) || {};
        var zipMatch = /ZIP_(\d{5})/.exec(attrs._order_tags || '');
        if (zipMatch) cartZip = zipMatch[1];

        var fp = cartZip + '::' + items.map(function (i) { return i.handle; }).sort().join('|');
        if (fp === lastFingerprint) { scheduleApply(); return ruleActive; }
        lastFingerprint = fp;

        if (!items.length) { ruleActive = false; scheduleApply(); return false; }

        var uniq = items.map(function (i) { return i.handle; }).filter(Boolean)
          .filter(function (h, idx, a) { return a.indexOf(h) === idx; });
        return Promise.all(uniq.map(productHasTag)).then(function (results) {
          ruleActive = results.some(Boolean);
          log('재평가 — 카트에 leadtime:', ruleActive, '| zone:', getZone(), '| target:', getTargetStr());
          scheduleApply();
          return ruleActive;
        });
      })
      .catch(function (e) { log('refresh 오류', e); return false; });
  }

  // ============================================================
  // 캘린더 적용 — 목표일 1개만 남기고 숨김 (그날만). 클릭/리로드 없음.
  // ============================================================
  var observer = null;
  var applyTimer = null;

  function scheduleApply() {
    if (applyTimer) clearTimeout(applyTimer);
    applyTimer = setTimeout(doApply, 80);
  }

  function doApply() {
    var options = document.querySelectorAll('.date-option');
    if (!options.length) return;

    if (observer) observer.disconnect();

    var target = ruleActive ? getTargetStr() : null;

    if (!target) {
      restore();
    } else {
      // 목표일이 캘린더에 실제 있는지 확인 (없으면 fail-open: 손대지 않음)
      var inCalendar = false;
      for (var k = 0; k < options.length; k++) {
        if (options[k].dataset.date === target) { inCalendar = true; break; }
      }
      if (!inCalendar) {
        restore();
        log('⚠️ 목표일', target, '이 캘린더에 없음 → 제한 보류(날짜 확인 필요)');
      } else {
        for (var i = 0; i < options.length; i++) {
          var o = options[i];
          var ds = o.dataset.date;
          if (!ds) continue;
          if (ds === target) {
            if (o.classList.contains('dlm-hidden')) o.classList.remove('dlm-hidden');
            if (o.style.display === 'none') o.style.display = '';
          } else {
            if (o.style.display !== 'none') o.style.display = 'none';
            o.classList.add('dlm-hidden');
            o.classList.remove('selected');
          }
        }
        if (CONFIG.showNotice) updateNotice(target); else removeNotice();
        log('적용 — 그날만:', target);
      }
    }

    reconnectObserver();
  }

  function restore() {
    var hidden = document.querySelectorAll('.date-option.dlm-hidden');
    for (var i = 0; i < hidden.length; i++) {
      hidden[i].classList.remove('dlm-hidden');
      if (hidden[i].style.display === 'none') hidden[i].style.display = '';
    }
    removeNotice();
  }

  // ============================================================
  // 안내 문구 (주황 한 줄)
  // ============================================================
  function noticeText(targetStr) {
    var p = targetStr.split('-');
    var d = new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
    var dn = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var mn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var label = dn[d.getDay()] + ', ' + mn[d.getMonth()] + ' ' + d.getDate();
    return CONFIG.noticeText.replace('{date}', label);
  }

  function updateNotice(targetStr) {
    var options = document.querySelectorAll('.date-option');
    if (!options.length) return;
    var container = options[0].parentNode;
    if (!container || !container.parentNode) return;
    var notice = document.getElementById('dlm-notice');
    if (!notice) {
      notice = document.createElement('div');
      notice.id = 'dlm-notice';
      notice.style.cssText =
        'margin:2px 0 4px;font-size:13px;line-height:1.4;color:#FF4713;font-weight:500;';
      container.parentNode.insertBefore(notice, container);
    }
    var t = noticeText(targetStr);
    if (notice.textContent !== t) notice.textContent = t;
  }

  function removeNotice() {
    var n = document.getElementById('dlm-notice');
    if (n && n.parentNode) n.parentNode.removeChild(n);
  }

  // ============================================================
  // 판매 마감 — 상품 페이지에서 leadtime 상품이고 마감시각 지났으면
  //            장바구니 담기 버튼 비활성 + 'Order closed' 표시
  // ============================================================
  function applyProductCutoff() {
    var m = /\/products\/([^\/\?#]+)/.exec(location.pathname);
    if (!m) return; // 상품 페이지 아님
    var handle = m[1];

    if (!isClosed()) { unblockProduct(); return; }

    productHasTag(handle).then(function (has) {
      if (has) blockProduct();
    });
  }

  function addToCartButtons() {
    return document.querySelectorAll(
      'form[action*="/cart/add"] [type="submit"], form[action*="/cart/add"] button[name="add"], ' +
      '.product-form__submit, button.product-form__submit, [data-add-to-cart]'
    );
  }

  function blockProduct() {
    var btns = addToCartButtons();
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (b.dataset.dlmBlocked) continue;
      b.dataset.dlmBlocked = '1';
      b.dataset.dlmText = b.innerHTML;
      b.disabled = true;
      b.style.opacity = '0.5';
      b.style.cursor = 'not-allowed';
      b.innerHTML = 'Sold out';
    }
    // 마감 안내 한 줄
    if (btns.length && !document.getElementById('dlm-closed-msg')) {
      var msg = document.createElement('div');
      msg.id = 'dlm-closed-msg';
      msg.textContent = CONFIG.closedText;
      msg.style.cssText = 'margin:8px 0;font-size:13px;color:#FF4713;font-weight:500;';
      var ref = btns[0].closest('form') || btns[0];
      ref.parentNode.insertBefore(msg, ref);
    }
    log('🚫 판매 마감 — 담기 버튼 비활성:', handleFromPath());
  }

  function unblockProduct() {
    var btns = document.querySelectorAll('[data-dlm-blocked="1"]');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      b.disabled = false;
      b.style.opacity = '';
      b.style.cursor = '';
      if (b.dataset.dlmText) b.innerHTML = b.dataset.dlmText;
      delete b.dataset.dlmBlocked;
      delete b.dataset.dlmText;
    }
    var msg = document.getElementById('dlm-closed-msg');
    if (msg && msg.parentNode) msg.parentNode.removeChild(msg);
  }

  function handleFromPath() {
    var m = /\/products\/([^\/\?#]+)/.exec(location.pathname);
    return m ? m[1] : '';
  }

  // ============================================================
  // 트리거
  // ============================================================
  function reconnectObserver() {
    var root = document.querySelector('.date-option');
    var target = (root && root.parentNode && root.parentNode.parentNode) || document.body;
    if (!observer) observer = new MutationObserver(function () { scheduleApply(); });
    observer.observe(target, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['style', 'class']
    });
  }

  function bind() {
    document.addEventListener('calendarRendered', function () { setTimeout(refresh, 350); });

    if (window.fetch && !window.__dlmFetchPatched) {
      window.__dlmFetchPatched = true;
      var of = window.fetch;
      window.fetch = function () {
        var p = of.apply(this, arguments);
        try {
          var url = arguments[0];
          var u = typeof url === 'string' ? url : (url && url.url) || '';
          if (/\/cart\/(add|change|update|clear)/i.test(u)) p.then(function () { setTimeout(refresh, 250); });
        } catch (e) { /* noop */ }
        return p;
      };
    }

    reconnectObserver();
    setTimeout(refresh, 400);
    applyProductCutoff();
    // 자정 넘어 마감시각 지나는 경우 대비 — 5분마다 마감 재확인(상품 페이지)
    setInterval(applyProductCutoff, 5 * 60 * 1000);
  }

  // ============================================================
  // 공개 API (콘솔 테스트용)
  // ============================================================
  window.DeliveryLeadtimeManager = {
    version: '2.0',
    config: CONFIG,
    refresh: refresh,
    getDrop: getDrop,
    getZone: getZone,
    getTargetStr: getTargetStr,
    isClosed: isClosed,
    _state: function () {
      return { ruleActive: ruleActive, zone: getZone(), drop: getDrop(), target: getTargetStr(), closed: isClosed() };
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }

  log('✅ V2.0 Ready — metaobject limited_drop 기반 | tag:', CONFIG.tag);
})();